import React, { useCallback, useMemo, useRef } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch } from "react-redux";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { setShowPotCollectionReload, setShowPotCollectionGrandJackpot, setShowPotCollectionMaxWin, setShowPotCollectionOutro } from "../../../../State/Slices/animationsSlice";

// Panel configuration
const PANEL_CONFIG = {
  PADDING: 16,
  BUTTON_WIDTH: 160,
  BUTTON_HEIGHT: 44,
  BUTTON_GAP: 10,
  COLUMNS: 2,
  BORDER_RADIUS: 10,
  PANEL_ALPHA: 0.85,
  PANEL_COLOR: 0x1e1e2e,
  BUTTON_COLOR: 0x3a3a5c,
  BUTTON_ACTIVE_COLOR: 0x1a6b4a,
  BUTTON_HOVER_COLOR: 0x4a4a6e,
  TEXT_COLOR: 0xffffff,
  ACTIVE_TEXT_COLOR: 0x5effa0,
  TITLE_COLOR: 0xaaaacc,
  FONT_SIZE: 14,
  TITLE_FONT_SIZE: 11,
};

/**
 * DevPanel — Development test panel.
 * Gated by FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL.
 * Only for testing popups — no transition/tension triggers.
 */
const DevPanel = () => {
  const dispatch = useDispatch();

  const hoverRefs = useRef({});
  const graphicsRefs = useRef({});

  const buttons = useMemo(
    () => [
      {
        id: "reload_popup",
        label: "Reload Popup",
        active: false,
        action: () => dispatch(setShowPotCollectionReload(true)),
      },
      {
        id: "gjp_popup",
        label: "GJP Popup",
        active: false,
        action: () => dispatch(setShowPotCollectionGrandJackpot(true)),
      },
      {
        id: "max_win",
        label: "Max Win",
        active: false,
        action: () => dispatch(setShowPotCollectionMaxWin(true)),
      },
      {
        id: "total_win",
        label: "Total Win",
        active: false,
        action: () => dispatch(setShowPotCollectionOutro(true)),
      },
      {
        id: "all_popups",
        label: "▶ ALL Popups",
        active: false,
        action: () => {
          dispatch(setShowPotCollectionReload(true));
          setTimeout(() => dispatch(setShowPotCollectionGrandJackpot(true)), 500);
          setTimeout(() => dispatch(setShowPotCollectionMaxWin(true)), 1000);
          setTimeout(() => dispatch(setShowPotCollectionOutro(true)), 1500);
        },
      },
    ],
    [dispatch]
  );

  const { PADDING, BUTTON_WIDTH, BUTTON_HEIGHT, BUTTON_GAP, COLUMNS, BORDER_RADIUS } = PANEL_CONFIG;

  const rows = Math.ceil(buttons.length / COLUMNS);
  const panelWidth = PADDING * 2 + COLUMNS * BUTTON_WIDTH + (COLUMNS - 1) * BUTTON_GAP;
  const titleHeight = 20;
  const panelHeight = PADDING * 2 + titleHeight + rows * BUTTON_HEIGHT + (rows - 1) * BUTTON_GAP;

  const panelX = 10;
  const panelY = 10;

  const titleStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: PANEL_CONFIG.TITLE_FONT_SIZE,
        fontWeight: "600",
        fill: PANEL_CONFIG.TITLE_COLOR,
        letterSpacing: 1,
      }),
    []
  );

  const drawPanel = useCallback(
    (g) => {
      g.clear();
      g.beginFill(PANEL_CONFIG.PANEL_COLOR, PANEL_CONFIG.PANEL_ALPHA);
      g.lineStyle(1, 0x333355, 0.5);
      g.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, BORDER_RADIUS);
      g.endFill();
    },
    [panelX, panelY, panelWidth, panelHeight, BORDER_RADIUS]
  );

  const drawButton = useCallback(
    (g, btnIndex) => {
      const btn = buttons[btnIndex];
      const col = btnIndex % COLUMNS;
      const row = Math.floor(btnIndex / COLUMNS);
      const bx = panelX + PADDING + col * (BUTTON_WIDTH + BUTTON_GAP);
      const by = panelY + PADDING + titleHeight + row * (BUTTON_HEIGHT + BUTTON_GAP);
      const isHovered = hoverRefs.current[btn.id] || false;
      const color = btn.active
        ? PANEL_CONFIG.BUTTON_ACTIVE_COLOR
        : isHovered
          ? PANEL_CONFIG.BUTTON_HOVER_COLOR
          : PANEL_CONFIG.BUTTON_COLOR;

      g.clear();
      g.beginFill(color, 0.9);
      g.lineStyle(1, btn.active ? PANEL_CONFIG.ACTIVE_TEXT_COLOR : 0x444466, 0.6);
      g.drawRoundedRect(bx, by, BUTTON_WIDTH, BUTTON_HEIGHT, 4);
      g.endFill();
    },
    [buttons, panelX, panelY, PADDING, BUTTON_WIDTH, BUTTON_HEIGHT, BUTTON_GAP, COLUMNS]
  );

  return (
    <Container name="dev_panel" zIndex={9999} sortableChildren>
      <Graphics draw={drawPanel} />
      <Text
        text="DEV PANEL"
        style={titleStyle}
        x={panelX + PADDING}
        y={panelY + PADDING}
        resolution={2}
      />
      {buttons.map((btn, i) => {
        const col = i % COLUMNS;
        const row = Math.floor(i / COLUMNS);
        const bx = panelX + PADDING + col * (BUTTON_WIDTH + BUTTON_GAP);
        const by = panelY + PADDING + titleHeight + row * (BUTTON_HEIGHT + BUTTON_GAP);

        const btnTextStyle = new PIXI.TextStyle({
          fontFamily: FONT_CONFIG.PRIMARY,
          fontSize: PANEL_CONFIG.FONT_SIZE,
          fontWeight: "700",
          fill: btn.active ? PANEL_CONFIG.ACTIVE_TEXT_COLOR : PANEL_CONFIG.TEXT_COLOR,
        });

        return (
          <Container key={btn.id} name={`dev_btn_${btn.id}`}>
            <Graphics
              ref={(ref) => { if (ref) graphicsRefs.current[btn.id] = ref; }}
              draw={(g) => drawButton(g, i)}
              eventMode="static"
              cursor="pointer"
              hitArea={new PIXI.Rectangle(bx, by, BUTTON_WIDTH, BUTTON_HEIGHT)}
              onpointertap={(e) => { e.stopPropagation(); btn.action(); }}
              onpointerover={() => {
                hoverRefs.current[btn.id] = true;
                const gfx = graphicsRefs.current[btn.id];
                if (gfx) drawButton(gfx, i);
              }}
              onpointerout={() => {
                hoverRefs.current[btn.id] = false;
                const gfx = graphicsRefs.current[btn.id];
                if (gfx) drawButton(gfx, i);
              }}
            />
            <Text
              text={btn.active ? `● ${btn.label}` : btn.label}
              style={btnTextStyle}
              anchor={0.5}
              x={bx + BUTTON_WIDTH / 2}
              y={by + BUTTON_HEIGHT / 2}
              resolution={2}
              eventMode="none"
            />
          </Container>
        );
      })}
    </Container>
  );
};

export default DevPanel;
